'use strict';

const libPictProvider = require('pict-provider');

// The Default-ChangeOrder HeadlightDynamicForm manifest (extracted from ContractAdminConfig getChangeOrderFormRNI).
// Rendered by the ChangeOrderFormHost (pict-section-form-host) for create/edit.
const CHANGE_ORDER_MANIFEST = require('../change-orders/Default-ChangeOrder-Manifest.json');
const libProjectChangeOrderGating = require('../change-orders/ProjectChangeOrderGating.js');

const BODY_MOUNT = '#CO-Body';

// Coarse ChangeOrder header statuses (the approval lifecycle) → a color class for the status pill.
const STATUS_CLASS =
{
	'Draft': 'co-st-draft',
	'Pending PE': 'co-st-pending', 'Pending CE': 'co-st-pending', 'Pending CA': 'co-st-pending',
	'Submitted': 'co-st-submitted',
	'Approved': 'co-st-approved',
	'Rejected': 'co-st-rejected',
};

/**
 * ChangeOrdersProvider — data + render for the ProjectWorkspace Contract ▸ Change Orders tab. Phase 1: list /
 * browse the project's ChangeOrder headers and view one (its header + LineItemChangeOrder lines + linked form).
 *
 * A change order is a GROUP: one ChangeOrder header (ChangeNumber, Amount, Status, dates, FormIDDocument) groups
 * many LineItemChangeOrder rows via IDChangeOrder. Both are plain Meadow entities, read customer/project-scoped.
 * The surface honors the same `enableNewChangeOrders` config the contract_admin app reads
 * (Configuration/For/ApplicationSettings → ContractAdmin, with a per-project override). Create/edit (the Tidings
 * change-order form via pict-section-form-host) + the project-status line-item gating arrive in Phase 2.
 */
class ChangeOrdersProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
		this._idProject = 0;
		this.hasModel = false;
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }
	get permissions() { return this.pict.fable.EffectivePermissionsService; }
	get idProject() { return this._idProject; }

	get state()
	{
		if (!this.pict.AppData.ChangeOrders)
		{
			this.pict.AppData.ChangeOrders =
			{
				Mode: 'list', Loading: false, Message: '', ProjectName: '', IDProject: 0,
				EnableNewChangeOrders: true, Rows: [], Filter: '', ActiveCO: null, Lines: [],
			};
		}
		return this.pict.AppData.ChangeOrders;
	}

	_getJSON(pURL) { return new Promise((resolve, reject) => this.restClient.getJSON(pURL, (pErr, pBody) => (pErr ? reject(pErr) : resolve(pBody)))); }
	_postJSON(pURL, pBody) { return new Promise((resolve, reject) => this.restClient.postJSON(pURL, pBody, (pErr, pRes) => (pErr ? reject(pErr) : resolve(pRes)))); }
	_putJSON(pURL, pBody) { return new Promise((resolve, reject) => this.restClient.putJSON(pURL, pBody, (pErr, pRes) => (pErr ? reject(pErr) : resolve(pRes)))); }

	_toast(pMessage, pType) { if (this.modal && (typeof this.modal.toast === 'function')) { this.modal.toast(pMessage, { type: pType || 'info' }); } }

	/** Read-only gate (Phase 1 lists + views); fail-closed until the capability map is ready. */
	canRead()
	{
		const tmpPermissions = this.permissions;
		if (!tmpPermissions || (typeof tmpPermissions.canHash !== 'function') || !tmpPermissions.currentUserReady()) { return true; }
		return tmpPermissions.canHash('ChangeOrder-Read');
	}

	/** Create gate — ChangeOrder-Create; fail-closed once the capability map is ready. */
	canCreate()
	{
		const tmpPermissions = this.permissions;
		if (!tmpPermissions || (typeof tmpPermissions.canHash !== 'function') || !tmpPermissions.currentUserReady()) { return true; }
		return tmpPermissions.canHash('ChangeOrder-Create');
	}

	_esc(pValue) { return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }

	_money(pValue)
	{
		const tmpN = Number(pValue);
		if (!Number.isFinite(tmpN)) { return '—'; }
		try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(tmpN); }
		catch (pError) { return '$' + tmpN.toFixed(2); }
	}

	_date(pValue)
	{
		const tmpString = String(pValue == null ? '' : pValue);
		if (!tmpString || tmpString.indexOf('0000-00-00') === 0) { return '—'; }
		const tmpDate = new Date(tmpString);
		return Number.isNaN(tmpDate.getTime()) ? tmpString : tmpDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
	}

	// --- Rendering seam: the view repaints the template into its (embedded) host, then paints the body here. ---

	_render() { if (this.pict.views['ChangeOrders']) { this.pict.views['ChangeOrders'].render(); } }

	renderBody()
	{
		if (this.pict.ContentAssignment.getElement(BODY_MOUNT).length === 0) { return; }
		this.pict.ContentAssignment.assignContent(BODY_MOUNT, this._bodyHTML());
		// In form mode a re-paint recreates an empty #CO-FormHost; if the form host already has a manifest loaded
		// (e.g. a workspace repaint fired), re-render it into the fresh host so the form isn't wiped.
		if ((this.state.Mode === 'form') && this.formHost && (typeof this.formHost.isLoaded === 'function') && this.formHost.isLoaded()
			&& (this.pict.ContentAssignment.getElement('#CO-FormHost').length > 0))
		{
			try { this.formHost.render(); } catch (pError) { /* best-effort re-mount */ }
		}
	}

	// --- Config: the same enableNewChangeOrders the contract_admin app reads (customer + per-project override) ---

	async _loadEnableNewChangeOrdersAsync()
	{
		try
		{
			const tmpConfig = await this._getJSON(`Configuration/For/ApplicationSettings/ContractAdmin/IDProject/${this._idProject}`);
			// The scoped endpoint returns the ContractAdmin block directly (project override folded over customer).
			if (tmpConfig && (typeof tmpConfig.enableNewChangeOrders !== 'undefined')) { return !!tmpConfig.enableNewChangeOrders; }
			// Fallback shape: the full ApplicationSettings document with a ContractAdmin sub-object.
			if (tmpConfig && tmpConfig.ContractAdmin && (typeof tmpConfig.ContractAdmin.enableNewChangeOrders !== 'undefined')) { return !!tmpConfig.ContractAdmin.enableNewChangeOrders; }
		}
		catch (pError) { /* config unavailable — default on (the reference treats a missing flag as the new flow off, but here the tab is already gated by presence; default to showing the list) */ }
		return true;
	}

	/**
	 * The two-mode gating (same as contract_admin's ProjectChangeOrderGating): the project requires a change order
	 * for line-item edits when its Status is in the ProjectType's RequiresChangeOrder list. Also discover the
	 * project's change-order form RNI (the Document DocumentType used on create/edit).
	 */
	async _loadGatingAsync()
	{
		this._coRNIHash = '';
		const tmpGating = await libProjectChangeOrderGating.fetchProjectChangeOrderGating((pURL) => this._getJSON(pURL), this._idProject);
		try
		{
			const tmpRNIs = await this._getJSON(`ReportNamedInstancesByProject/${this._idProject}/0/250`);
			const tmpCO = (Array.isArray(tmpRNIs) ? tmpRNIs : []).filter((pRNI) =>
			{
				try { const tmpOpt = (typeof pRNI.Options === 'string') ? JSON.parse(pRNI.Options) : (pRNI.Options || {}); return tmpOpt.FormOnly && tmpOpt.TidingsData && (String(tmpOpt.TidingsData.Type || '').toLowerCase().indexOf('changeorder') > -1); }
				catch (pInner) { return false; }
			});
			// Prefer an active (non-archived) change-order form.
			const tmpActive = tmpCO.find((pRNI) => !/DO NOT USE|ARCHIV/i.test(pRNI.Name || '')) || tmpCO[0];
			this._coRNIHash = tmpActive ? tmpActive.Hash : '';
		}
		catch (pError) { /* no RNI discoverable — create/edit will surface a clear message */ }
		this.state.RequiresChangeOrder = !!tmpGating.requires;
		this.state.ProjectStatus = tmpGating.status || '';
	}

	// --- Load ---

	async loadAsync(pIDProject)
	{
		this._idProject = Number(pIDProject) || 0;
		const tmpState = this.state;
		tmpState.IDProject = this._idProject;
		tmpState.Mode = 'list';
		tmpState.Loading = true;
		tmpState.Message = '';
		this.hasModel = false;
		this._render();
		try
		{
			const tmpResults = await Promise.all(
			[
				this._loadEnableNewChangeOrdersAsync(),
				this._loadGatingAsync().then(() => true).catch(() => true),
				this._getJSON(`ChangeOrders/FilteredTo/FBV~IDProject~EQ~${this._idProject}~FBV~Deleted~EQ~0~FSF~DateOfChange~DESC~0/0/500`),
			]);
			tmpState.EnableNewChangeOrders = tmpResults[0];
			const tmpRows = tmpResults[2];
			tmpState.Rows = (Array.isArray(tmpRows) ? tmpRows : []);
			this.hasModel = true;
			tmpState.Loading = false;
			this._render();
		}
		catch (pError)
		{
			tmpState.Loading = false;
			tmpState.Message = `Could not load change orders: ${(pError && pError.message) ? pError.message : pError}`;
			this._render();
		}
	}

	/** Open one change order's detail (its header + its LineItemChangeOrder line rows). */
	async openCO(pIDChangeOrder)
	{
		const tmpState = this.state;
		const tmpID = Number(pIDChangeOrder) || 0;
		tmpState.ActiveCO = (tmpState.Rows || []).find((pRow) => Number(pRow.IDChangeOrder) === tmpID) || null;
		tmpState.Mode = 'detail';
		tmpState.Loading = true;
		this._render();
		try
		{
			if (!tmpState.ActiveCO) { tmpState.ActiveCO = await this._getJSON(`ChangeOrder/${tmpID}`); }
			const tmpLines = await this._getJSON(`LineItemChangeOrders/FilteredTo/FBV~IDChangeOrder~EQ~${tmpID}~FBV~Deleted~EQ~0~FSF~ChangeNumber~ASC~0/0/500`);
			tmpState.Lines = (Array.isArray(tmpLines) ? tmpLines : []);
			tmpState.Loading = false;
			this._render();
		}
		catch (pError)
		{
			tmpState.Loading = false;
			tmpState.Message = `Could not load the change order: ${(pError && pError.message) ? pError.message : pError}`;
			this._render();
		}
	}

	backToList()
	{
		const tmpState = this.state;
		tmpState.Mode = 'list';
		tmpState.Message = '';
		tmpState.ActiveCO = null;
		tmpState.Lines = [];
		this._render();
	}

	// --- Create / edit via the change-order dynamic form (pict-section-form-host) ---

	get formHost() { return this.pict.providers.ChangeOrderFormHost; }

	/** Open a blank change-order form (create). Paints the form panel, then mounts the form into #CO-FormHost. */
	startCreate()
	{
		const tmpState = this.state;
		tmpState.Mode = 'form';
		tmpState.FormMode = 'create';
		tmpState.FormTitle = 'New Change Order';
		tmpState.Message = '';
		this._editingIDDocument = 0;
		this.pict.AppData.ChangeOrderForm = {};
		this._render();
		this._mountForm({});
	}

	/** Mount (or remount) the change-order form manifest into the freshly-painted #CO-FormHost. */
	_mountForm(pData)
	{
		const tmpHost = this.formHost;
		if (!tmpHost || (typeof tmpHost.loadForm !== 'function'))
		{
			this.state.Message = 'The change-order form runner is not available.';
			this.renderBody();
			return;
		}
		try
		{
			this.pict.AppData.ChangeOrderForm = pData || {};
			tmpHost.loadForm(CHANGE_ORDER_MANIFEST, { DestinationAddress: '#CO-FormHost', DataAddress: 'AppData.ChangeOrderForm' });
		}
		catch (pError)
		{
			this.state.Message = `Could not open the change-order form: ${(pError && pError.message) ? pError.message : pError}`;
			this.renderBody();
		}
	}

	get modal() { return this.pict.views['Pict-Section-Modal']; }

	/**
	 * Save the change-order form as a Draft Document (the same shape contract_admin's FormCreate posts: DocumentType
	 * = the project's change-order form RNI, Form:1, FormData = the marshaled form). The server's DocumentApproval
	 * workflow materializes the ChangeOrder/LineItemChangeOrder rows on approval; a Draft just persists the form.
	 */
	async saveForm()
	{
		const tmpState = this.state;
		if (tmpState.Saving) { return; }
		const tmpHost = this.formHost;
		if (!tmpHost) { this._toast('The change-order form runner is not available.', 'error'); return; }
		if (!this._coRNIHash) { this._toast('No change-order form is configured for this project.', 'error'); return; }
		if (typeof tmpHost.save === 'function') { try { tmpHost.save(); } catch (pError) { /* marshal best-effort */ } }
		const tmpFormData = Object.assign({ Meta: { DocumentCreatorApp: 'Platform Configuration' } },
			(typeof tmpHost.getFormData === 'function') ? (tmpHost.getFormData() || {}) : (this.pict.AppData.ChangeOrderForm || {}));
		tmpState.Saving = true;
		try
		{
			const tmpNow = (new Date()).toISOString();
			let tmpSaved;
			if (this._editingIDDocument)
			{
				tmpSaved = await this._putJSON('Document', { IDDocument: Number(this._editingIDDocument), IDProject: this._idProject, DocumentType: this._coRNIHash, Form: 1, FormData: tmpFormData });
			}
			else
			{
				tmpSaved = await this._postJSON('Document',
					{ Name: `Change Order — ${tmpNow.slice(0, 10)}`, DocumentDate: tmpNow, IDProject: this._idProject, DocumentType: this._coRNIHash, Form: 1, FormData: tmpFormData, Status: 'Draft', Observations: [] });
			}
			if (!tmpSaved || tmpSaved.ErrorCode || tmpSaved.Error)
			{
				throw new Error((tmpSaved && (tmpSaved.Error || tmpSaved.ErrorCode)) ? String(tmpSaved.Error || tmpSaved.ErrorCode) : 'the server rejected the change order.');
			}
			tmpState.Saving = false;
			this._editingIDDocument = 0;
			this._toast('Change order draft saved.', 'success');
			this.loadAsync(this._idProject);   // back to the list, refreshed
		}
		catch (pError)
		{
			tmpState.Saving = false;
			this._toast(`Could not save the change order: ${(pError && pError.message) ? pError.message : pError}`, 'error');
		}
	}

	/** Edit an existing change order — reopen its form Document (FormIDDocument) in the runner. */
	async editCO(pIDChangeOrder)
	{
		const tmpState = this.state;
		const tmpCO = (tmpState.ActiveCO && Number(tmpState.ActiveCO.IDChangeOrder) === Number(pIDChangeOrder))
			? tmpState.ActiveCO
			: (tmpState.Rows || []).find((pRow) => Number(pRow.IDChangeOrder) === Number(pIDChangeOrder));
		if (!tmpCO || !tmpCO.FormIDDocument) { this._toast('This change order has no editable form.', 'info'); return; }
		tmpState.Mode = 'form';
		tmpState.FormMode = 'edit';
		tmpState.FormTitle = `Edit ${tmpCO.ChangeNumber || 'Change Order'}`;
		tmpState.Message = '';
		tmpState.Loading = true;
		this._render();
		try
		{
			const tmpDoc = await this._getJSON(`Document/${Number(tmpCO.FormIDDocument)}`);
			this._editingIDDocument = Number(tmpCO.FormIDDocument);
			tmpState.Loading = false;
			this._render();
			this._mountForm((tmpDoc && tmpDoc.FormData) ? tmpDoc.FormData : {});
		}
		catch (pError)
		{
			tmpState.Loading = false;
			tmpState.Message = `Could not load the change-order form: ${(pError && pError.message) ? pError.message : pError}`;
			this._render();
		}
	}

	cancelForm()
	{
		const tmpHost = this.formHost;
		if (tmpHost && (typeof tmpHost.destroy === 'function')) { try { tmpHost.destroy(); } catch (pError) { /* best-effort */ } }
		this._editingIDDocument = 0;
		this.state.Mode = 'list';
		this.state.Message = '';
		this._render();
	}

	/** In-place list filter (keeps the filter input focused — only the list body repaints). */
	onFilter(pValue)
	{
		this.state.Filter = pValue;
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement('#CO-List').length) { tmpCA.assignContent('#CO-List', this._listRowsHTML()); }
	}

	// --- HTML ---

	_bodyHTML()
	{
		const tmpState = this.state;
		if (!this.canRead()) { return `<div class="co-note co-note-locked"><i class="fas fa-lock"></i> You don’t have permission to view change orders.</div>`; }
		if (tmpState.Loading) { return `<div class="co-note"><i class="fas fa-spinner fa-spin"></i> Loading…</div>`; }
		if (tmpState.Message && tmpState.Mode !== 'form') { return `<div class="co-note co-note-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(tmpState.Message)}</div>`; }
		if (tmpState.Mode === 'form') { return this._formHTML(); }
		return (tmpState.Mode === 'detail') ? this._detailHTML() : this._listHTML();
	}

	_formHTML()
	{
		const tmpState = this.state;
		const tmpError = tmpState.Message ? `<div class="co-note co-note-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(tmpState.Message)}</div>` : '';
		return `<div class="co-head"><div><div class="co-eyebrow"><a class="co-back" onclick="_Pict.providers.ChangeOrders.cancelForm()"><i class="fas fa-arrow-left"></i> Change Orders</a></div>`
			+ `<h2 class="co-title">${this._esc(tmpState.FormTitle || 'Change Order')}</h2></div>`
			+ `<div class="co-head-actions"><button class="co-btn co-btn-primary" onclick="_Pict.providers.ChangeOrders.saveForm(); return false;">Save draft</button>`
			+ `<button class="co-btn" onclick="_Pict.providers.ChangeOrders.cancelForm(); return false;">Cancel</button></div></div>`
			+ tmpError
			+ `<div id="CO-FormHost" class="co-form-host"></div>`;
	}

	_listHTML()
	{
		const tmpState = this.state;
		const tmpGatedNote = tmpState.EnableNewChangeOrders ? ''
			: `<div class="co-note co-note-info"><i class="fas fa-circle-info"></i> The new Change Orders flow is not enabled for this project — showing existing change orders read-only.</div>`;
		// The two-mode indicator (projectRequiresChangeOrder): the project's status decides whether line-item edits go
		// direct or must be made through a change order.
		const tmpModeBanner = tmpState.RequiresChangeOrder
			? `<div class="co-mode co-mode-required"><i class="fas fa-lock"></i> This project’s status <strong>${this._esc(tmpState.ProjectStatus || '')}</strong> requires a <strong>Change Order</strong> to modify line items or bid items.</div>`
			: (tmpState.ProjectStatus ? `<div class="co-mode co-mode-direct"><i class="fas fa-pen-to-square"></i> Line items on this project (status <strong>${this._esc(tmpState.ProjectStatus)}</strong>) are directly editable; change orders are optional.</div>` : '');
		const tmpNewBtn = (tmpState.EnableNewChangeOrders && this.canCreate())
			? `<button class="co-btn co-btn-primary" onclick="_Pict.providers.ChangeOrders.startCreate(); return false;"><i class="fas fa-plus"></i> New Change Order</button>`
			: '';
		return `<div class="co-head"><div><div class="co-eyebrow">Contract</div><h2 class="co-title">Change Orders</h2></div>`
			+ `<div class="co-head-actions">${tmpNewBtn}<input class="co-filter" type="text" autocomplete="off" placeholder="Filter by number, status, description…" oninput="_Pict.providers.ChangeOrders.onFilter(this.value)" /></div></div>`
			+ tmpModeBanner
			+ tmpGatedNote
			+ `<div id="CO-List" class="co-list">${this._listRowsHTML()}</div>`;
	}

	_listRowsHTML()
	{
		const tmpState = this.state;
		const tmpTerm = String(tmpState.Filter || '').trim().toLowerCase();
		const tmpRows = (tmpState.Rows || []).filter((pRow) => !tmpTerm
			|| (String(pRow.ChangeNumber || '').toLowerCase().indexOf(tmpTerm) > -1)
			|| (String(pRow.Status || '').toLowerCase().indexOf(tmpTerm) > -1)
			|| (String(pRow.Description || '').toLowerCase().indexOf(tmpTerm) > -1));
		if (!tmpRows.length) { return `<div class="co-note">${tmpTerm ? 'No change orders match your filter.' : 'This project has no change orders yet.'}</div>`; }
		const tmpHeader = `<div class="co-row co-row-head"><span class="co-c-num">Change #</span><span class="co-c-desc">Description</span><span class="co-c-amt">Amount</span><span class="co-c-status">Status</span><span class="co-c-date">Date</span><span class="co-c-chev"></span></div>`;
		return tmpHeader + tmpRows.map((pRow) =>
		{
			const tmpStatus = String(pRow.Status || '');
			const tmpPill = tmpStatus ? `<span class="co-pill ${STATUS_CLASS[tmpStatus] || 'co-st-draft'}">${this._esc(tmpStatus)}</span>` : '—';
			return `<div class="co-row co-row-click" onclick="_Pict.providers.ChangeOrders.openCO(${Number(pRow.IDChangeOrder)})">`
				+ `<span class="co-c-num">${this._esc(pRow.ChangeNumber || `#${pRow.IDChangeOrder}`)}</span>`
				+ `<span class="co-c-desc">${this._esc(String(pRow.Description || '').slice(0, 120))}</span>`
				+ `<span class="co-c-amt">${this._money(pRow.Amount)}</span>`
				+ `<span class="co-c-status">${tmpPill}</span>`
				+ `<span class="co-c-date">${this._date(pRow.DateOfChange)}</span>`
				+ `<span class="co-c-chev"><i class="fas fa-chevron-right"></i></span>`
				+ `</div>`;
		}).join('');
	}

	_detailHTML()
	{
		const tmpState = this.state;
		const tmpCO = tmpState.ActiveCO || {};
		const tmpStatus = String(tmpCO.Status || '');
		const tmpPill = tmpStatus ? `<span class="co-pill ${STATUS_CLASS[tmpStatus] || 'co-st-draft'}">${this._esc(tmpStatus)}</span>` : '';
		const fField = (pLabel, pValue) => `<div class="co-field"><span class="co-field-label">${this._esc(pLabel)}</span><span class="co-field-value">${pValue}</span></div>`;
		const tmpFormLink = tmpCO.FormIDDocument
			? `<a class="co-form-link" href="#/PSRS/Document/View/${Number(tmpCO.FormIDDocument)}"><i class="fas fa-file-lines"></i> Change Order Form #${Number(tmpCO.FormIDDocument)}</a>`
			: '<span class="co-field-value">—</span>';
		const tmpEditBtn = (tmpState.EnableNewChangeOrders && this.canCreate() && tmpCO.FormIDDocument)
			? `<button class="co-btn co-btn-primary" onclick="_Pict.providers.ChangeOrders.editCO(${Number(tmpCO.IDChangeOrder)}); return false;"><i class="fas fa-pen"></i> Edit form</button>`
			: '';
		return `<div class="co-head"><div><div class="co-eyebrow"><a class="co-back" onclick="_Pict.providers.ChangeOrders.backToList()"><i class="fas fa-arrow-left"></i> Change Orders</a></div>`
			+ `<h2 class="co-title">${this._esc(tmpCO.ChangeNumber || `Change Order #${tmpCO.IDChangeOrder}`)} ${tmpPill}</h2></div>`
			+ `<div class="co-head-actions">${tmpEditBtn}</div></div>`
			+ `<div class="co-detail-grid">`
			+ fField('Amount', this._money(tmpCO.Amount))
			+ fField('Reason Code', this._esc(tmpCO.ReasonCode || '—'))
			+ fField('Additional Days', this._esc((tmpCO.AdditionalDays != null && tmpCO.AdditionalDays !== '') ? tmpCO.AdditionalDays : '—'))
			+ fField('Date of Change', this._date(tmpCO.DateOfChange))
			+ fField('Approved', this._date(tmpCO.ApprovedByDate))
			+ fField('Form', tmpFormLink)
			+ `</div>`
			+ (tmpCO.Description ? `<div class="co-block"><div class="co-block-label">Description</div><div class="co-block-body">${this._esc(tmpCO.Description)}</div></div>` : '')
			+ (tmpCO.Remarks ? `<div class="co-block"><div class="co-block-label">Remarks</div><div class="co-block-body">${this._esc(tmpCO.Remarks)}</div></div>` : '')
			+ `<div class="co-block-label co-lines-label">Line Items <span class="co-lines-count">${(tmpState.Lines || []).length}</span></div>`
			+ this._linesHTML();
	}

	_linesHTML()
	{
		const tmpLines = this.state.Lines || [];
		if (!tmpLines.length) { return `<div class="co-note">This change order has no line-item changes.</div>`; }
		const tmpHeader = `<div class="co-lrow co-lrow-head"><span class="co-l-num">#</span><span class="co-l-name">Item</span><span class="co-l-qty">Change Qty</span><span class="co-l-cpu">Cost / Unit</span><span class="co-l-amt">Amount</span><span class="co-l-status">Status</span></div>`;
		return `<div class="co-lines">` + tmpHeader + tmpLines.map((pLine) =>
			`<div class="co-lrow">`
			+ `<span class="co-l-num">${this._esc(pLine.ChangeNumber || '')}</span>`
			+ `<span class="co-l-name">${this._esc(pLine.Name || pLine.ItemNumber || `Item ${pLine.IDLineItem || ''}`)}</span>`
			+ `<span class="co-l-qty">${this._esc((pLine.ChangeQuantity != null && pLine.ChangeQuantity !== '') ? pLine.ChangeQuantity : '—')}</span>`
			+ `<span class="co-l-cpu">${this._money(pLine.CostPerUnit)}</span>`
			+ `<span class="co-l-amt">${this._money(pLine.Amount)}</span>`
			+ `<span class="co-l-status">${this._esc(pLine.CommitmentChangeStatus || '—')}</span>`
			+ `</div>`).join('') + `</div>`;
	}
}

module.exports = ChangeOrdersProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'ChangeOrders',
	AutoInitialize: true,
	AutoInitializeOrdinal: 228,
};
